import React, { useState, useEffect } from "react";
import {
  FaUsers,
  FaCheckCircle,
  FaExclamationTriangle,
  FaToggleOn,
  FaToggleOff,
  FaEye,
  FaCalendarAlt,
  FaBriefcase,
  FaFilter,
  FaSync,
  FaUserCheck,
  FaUserTimes,
} from "react-icons/fa";
import StudentDetailsModal from "./StudentDetailsModal";
import StatsCards from "./StatsCards";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Application {
  id: number;
  companyName: string;
  positionProgram: string;
  dateApplied: string;
  applicationStatus: string;
  feedbackReceived: string;
  nextAction: string;
  nextActionDue: string;
  contactPerson: string;
  contactEmail: string;
  notes: string;
  coverLetterDownloadUrl?: string;
}

interface StudentOverview {
  studentId: number;
  studentName: string;
  studentEmail: string;
  isActivated: boolean;
  totalApplications: number;
  todayApplicationsCount: number;
  todayTargetMet: boolean;
  missedTargetDaysCount: number;
  missedTargetDates: string[];
  allApplications: Application[];
}

type TabType = "activated" | "notActivated";

const AdminAusbildungDashboard: React.FC = () => {
  const [students, setStudents] = useState<StudentOverview[]>([]);
  const [selectedStudent, setSelectedStudent] =
    useState<StudentOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("activated");
  const [filterCompliance, setFilterCompliance] = useState<
    "all" | "compliant" | "nonCompliant"
  >("all");
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetailsId, setLoadingDetailsId] = useState<number | null>(null);
  const [searchTermActivated, setSearchTermActivated] = useState("");
  const [searchTermNotActivated, setSearchTermNotActivated] = useState("");
  const [currentPageActivated, setCurrentPageActivated] = useState(1);
  const [currentPageNotActivated, setCurrentPageNotActivated] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchAllStudents();
  }, []);

  const fetchAllStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE}/api/ausbildung-tracking/admin/all-students`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch students data");
      }

      const data = await response.json();
      setStudents(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActivation = async (studentId: number) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/ausbildung-tracking/admin/toggle-activation/${studentId}`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to toggle activation");
      }

      await fetchAllStudents();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to toggle activation");
    }
  };

  const handleViewDetails = async (studentId: number) => {
    if (loadingDetailsId !== null) {
      return;
    }

    try {
      setLoadingDetailsId(studentId);

      const response = await fetch(
        `${API_BASE}/api/ausbildung-tracking/admin/student/${studentId}`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch student details");
      }

      const data = await response.json();
      setSelectedStudent(data);
      setShowDetailModal(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to fetch details");
    } finally {
      setLoadingDetailsId(null);
    }
  };

  const getCurrentTabStudents = () => {
    return students.filter((s) =>
      activeTab === "activated" ? s.isActivated : !s.isActivated,
    );
  };

  const getFilteredStudents = () => {
    let filtered = getCurrentTabStudents();
    const searchTerm =
      activeTab === "activated" ? searchTermActivated : searchTermNotActivated;

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.studentName.toLowerCase().includes(search) ||
          s.studentEmail.toLowerCase().includes(search),
      );
    }

    if (activeTab === "activated") {
      if (filterCompliance === "compliant") {
        filtered = filtered.filter((s) => s.todayTargetMet);
      } else if (filterCompliance === "nonCompliant") {
        filtered = filtered.filter((s) => !s.todayTargetMet);
      }
    }

    return filtered;
  };

  const filteredStudents = getFilteredStudents();

  const currentPage =
    activeTab === "activated" ? currentPageActivated : currentPageNotActivated;

  const handlePageChange = (page: number) => {
    if (activeTab === "activated") {
      setCurrentPageActivated(page);
    } else {
      setCurrentPageNotActivated(page);
    }
  };

  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex);

  useEffect(() => {
    if (activeTab === "activated") {
      setCurrentPageActivated(1);
    } else {
      setCurrentPageNotActivated(1);
    }
  }, [
    searchTermActivated,
    searchTermNotActivated,
    filterCompliance,
    activeTab,
  ]);

  const getOverallStats = () => {
    const activatedStudents = students.filter((s) => s.isActivated);

    return {
      totalStudents: students.length,
      activatedStudents: activatedStudents.length,
      notActivatedStudents: students.filter((s) => !s.isActivated).length,
      todayCompliant: activatedStudents.filter((s) => s.todayTargetMet).length,
      totalApplicationsToday: activatedStudents.reduce(
        (sum, s) => sum + s.todayApplicationsCount,
        0,
      ),
      studentsWithMissedDays: activatedStudents.filter(
        (s) => s.missedTargetDaysCount > 0,
      ).length,
    };
  };

  const stats = getOverallStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaExclamationTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchAllStudents}
            className="bg-[#009fe3] text-white px-4 py-2 rounded-md hover:bg-[#007cb5]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const currentSearchTerm =
    activeTab === "activated" ? searchTermActivated : searchTermNotActivated;

  const handleSearchChange = (value: string) => {
    if (activeTab === "activated") {
      setSearchTermActivated(value);
    } else {
      setSearchTermNotActivated(value);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaBriefcase />}
        title="Ausbildung Application Tracker"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Ausbildung Tracking" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <StatsCards stats={stats} />

        <div className="bg-white rounded-t-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab("activated")}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "activated"
                    ? "border-[#009fe3] text-[#009fe3]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <FaUserCheck />
                Activated Students
                <span
                  className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    activeTab === "activated"
                      ? "bg-[#009fe3] text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {stats.activatedStudents}
                </span>
              </button>
              <button
                onClick={() => setActiveTab("notActivated")}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "notActivated"
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <FaUserTimes />
                Not Activated Students
                <span
                  className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    activeTab === "notActivated"
                      ? "bg-orange-500 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {stats.notActivatedStudents}
                </span>
              </button>
            </nav>
          </div>
        </div>

        <div className="bg-white shadow p-6 border-t">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by student name or email..."
                    value={currentSearchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  />
                  <FaFilter className="absolute left-3 top-3 text-gray-400" />
                </div>
              </div>

              {currentSearchTerm && (
                <button
                  onClick={() => handleSearchChange("")}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Clear Search
                </button>
              )}
            </div>

            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <FaFilter className="text-gray-500" />
                <span className="font-medium text-gray-700">Filters:</span>
              </div>

              {activeTab === "activated" && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">
                    Today's Compliance:
                  </label>
                  <select
                    value={filterCompliance}
                    onChange={(e) =>
                      setFilterCompliance(
                        e.target.value as "all" | "compliant" | "nonCompliant",
                      )
                    }
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  >
                    <option value="all">All Students</option>
                    <option value="compliant">Target Met</option>
                    <option value="nonCompliant">Target Not Met</option>
                  </select>
                </div>
              )}

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Per Page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    handlePageChange(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              <div className="ml-auto text-sm text-gray-600">
                Showing {startIndex + 1}-
                {Math.min(endIndex, filteredStudents.length)} of{" "}
                {filteredStudents.length} students
                {getCurrentTabStudents().length !== filteredStudents.length && (
                  <span className="text-gray-500">
                    {" "}
                    (filtered from {getCurrentTabStudents().length})
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-b-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {activeTab === "activated" ? "Activated" : "Not Activated"}{" "}
              Students Overview
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activation
                  </th>
                  {activeTab === "activated" && (
                    <>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Apps
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Today's Apps
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Today's Status
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Missed Days
                      </th>
                    </>
                  )}
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedStudents.length === 0 ? (
                  <tr>
                    <td
                      colSpan={activeTab === "activated" ? 7 : 3}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium">No students found</p>
                      <p className="text-sm mt-2">
                        {currentSearchTerm
                          ? "Try adjusting your search or filters"
                          : activeTab === "activated"
                            ? "No activated students yet"
                            : "No students pending activation"}
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedStudents.map((student) => (
                    <tr key={student.studentId} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {student.studentName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {student.studentEmail}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() =>
                            handleToggleActivation(student.studentId)
                          }
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                            student.isActivated
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : "bg-orange-100 text-orange-800 hover:bg-orange-200"
                          }`}
                        >
                          {student.isActivated ? (
                            <>
                              <FaToggleOn size={20} />
                              Activated
                            </>
                          ) : (
                            <>
                              <FaToggleOff size={20} />
                              Not Activated
                            </>
                          )}
                        </button>
                      </td>

                      {activeTab === "activated" && (
                        <>
                          <td className="px-6 py-4 text-center">
                            <span className="text-sm font-semibold text-gray-900">
                              {student.totalApplications}
                            </span>
                          </td>

                          <td className="px-6 py-4 text-center">
                            <div className="text-sm font-semibold text-gray-900">
                              {student.todayApplicationsCount} / 2
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className={`h-2 rounded-full ${
                                  student.todayTargetMet
                                    ? "bg-green-500"
                                    : "bg-yellow-500"
                                }`}
                                style={{
                                  width: `${Math.min(
                                    (student.todayApplicationsCount / 2) * 100,
                                    100,
                                  )}%`,
                                }}
                              ></div>
                            </div>
                          </td>

                          <td className="px-6 py-4 text-center">
                            {student.todayTargetMet ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                <FaCheckCircle className="mr-1" />
                                Met
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                <FaExclamationTriangle className="mr-1" />
                                Not Met
                              </span>
                            )}
                          </td>

                          <td className="px-6 py-4 text-center">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                                student.missedTargetDaysCount > 0
                                  ? "bg-red-100 text-red-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {student.missedTargetDaysCount}
                            </span>
                          </td>
                        </>
                      )}

                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleViewDetails(student.studentId)}
                          disabled={loadingDetailsId !== null}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            loadingDetailsId === student.studentId
                              ? "bg-gray-300 text-gray-500 cursor-wait"
                              : loadingDetailsId !== null
                                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                : "bg-[#009fe3] hover:bg-[#007cb5] text-white"
                          }`}
                        >
                          {loadingDetailsId === student.studentId ? (
                            <>
                              <FaSync className="animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <FaEye />
                              View Details
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredStudents.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                {totalPages <= 10 ? (
                  Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 border rounded-md text-sm font-medium ${
                          currentPage === page
                            ? "bg-[#009fe3] text-white border-[#009fe3]"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )
                ) : (
                  <>
                    {currentPage > 3 && (
                      <span className="text-gray-500">...</span>
                    )}
                    {Array.from({ length: 5 }, (_, i) => {
                      const page = currentPage - 2 + i;
                      if (page < 1 || page > totalPages) return null;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 border rounded-md text-sm font-medium ${
                            currentPage === page
                              ? "bg-[#009fe3] text-white border-[#009fe3]"
                              : "border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    {currentPage < totalPages - 2 && (
                      <span className="text-gray-500">...</span>
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    handlePageChange(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {showDetailModal && selectedStudent && (
        <StudentDetailsModal
          student={selectedStudent}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
};

export default AdminAusbildungDashboard;
